import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { nuclearn_api_url } from "src/App";

/**
 * Configurable shape of the "identify stages" step. The effective values are
 * resolved on the backend as defaults <- global (admin) <- user override.
 */
export interface StageConfig {
  enabled: boolean;
  button_label: string;
  button_loading_label: string;
  button_done_label: string;
  extraction_prompt: string;
  stage_noun_singular: string;
  stage_noun_plural: string;
  report_type_prefix: string;
  stage_report_suffix_label: string;
  persona_fk: number | null;
}

/** A partial override (PUT body, and raw stored global/user overrides). */
export type StageConfigOverride = Partial<StageConfig>;

export interface StageConfigResponse {
  // Effective config for the current user: defaults <- global <- user.
  effective: StageConfig;
  // Global config the admin edits: defaults <- global only (no user layer).
  global_effective: StageConfig;
  // Hardcoded defaults — the diff baseline for the global form.
  defaults: StageConfig;
  global_override: StageConfigOverride | null;
  user_override: StageConfigOverride | null;
  is_admin: boolean;
}

const STAGE_CONFIG_QUERY_KEY = ["proposalGenerationStageConfig"];
// Computed at call time (not module load) to avoid a temporal-dead-zone error:
// `nuclearn_api_url` is initialized in src/App, which may not have run yet when
// this module is first evaluated through a circular import.
const base = () =>
  `${nuclearn_api_url}/products/proposal-generation/stage-config`;

const getStageConfig = async (): Promise<StageConfigResponse> => {
  const response = await axios.get(base());
  return response.data;
};

export const useGetStageConfig = () =>
  useQuery(STAGE_CONFIG_QUERY_KEY, getStageConfig, {
    staleTime: 5 * 60 * 1000,
  });

const updateGlobalStageConfig = async (
  payload: StageConfigOverride,
): Promise<StageConfigResponse> => {
  const response = await axios.put(`${base()}/global`, payload);
  return response.data;
};

export const useUpdateGlobalStageConfig = () => {
  const queryClient = useQueryClient();
  return useMutation(updateGlobalStageConfig, {
    onSuccess: (data) => {
      queryClient.setQueryData(STAGE_CONFIG_QUERY_KEY, data);
    },
  });
};

const updateUserStageConfig = async (
  payload: StageConfigOverride,
): Promise<StageConfigResponse> => {
  const response = await axios.put(`${base()}/user`, payload);
  return response.data;
};

export const useUpdateUserStageConfig = () => {
  const queryClient = useQueryClient();
  return useMutation(updateUserStageConfig, {
    onSuccess: (data) => {
      queryClient.setQueryData(STAGE_CONFIG_QUERY_KEY, data);
    },
  });
};

const resetUserStageConfig = async (): Promise<StageConfigResponse> => {
  const response = await axios.delete(`${base()}/user`);
  return response.data;
};

export const useResetUserStageConfig = () => {
  const queryClient = useQueryClient();
  return useMutation(resetUserStageConfig, {
    onSuccess: (data) => {
      queryClient.setQueryData(STAGE_CONFIG_QUERY_KEY, data);
    },
  });
};
